// src/pages/CaseStudy.jsx
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LocalizedLink from "../components/LocalizedLink";
import { CASES } from "../data/content";

export default function CaseStudy() {
  const { t } = useTranslation();
  const { slug } = useParams();
  const cs = CASES.find((c) => c.slug === slug);

  if (!cs) {
    return (
      <section className="mx-auto max-w-7xl px-4 py-16">
        <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-slate-700">
          {t("common.not_found") || "Not found"}
        </div>
      </section>
    );
  }

  const TT = (key, fallback = "") => {
    if (!key) return fallback;
    const v = t(key);
    return v === key ? fallback : v;
  };

  const title = TT(cs.titleKey, "");
  const tag = TT(cs.tagKey, "");
  const summary = TT(cs.summaryKey, "");
  const body = TT(cs.bodyKey, "");

  return (
    <section className="mx-auto max-w-4xl px-4 py-16">
      {/* Back link */}
      <LocalizedLink
        to="/projects"
        className="inline-flex items-center gap-2 text-sm text-slate-500 hover:text-slate-700"
      >
        <svg className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path d="M9.707 14.707a1 1 0 01-1.414 0L3.586 10l4.707-4.707a1 1 0 011.414 1.414L6.414 9H16a1 1 0 110 2H6.414l3.293 3.293a1 1 0 010 1.414z" />
        </svg>
        {t("projects.back")}
      </LocalizedLink>

      {/* Header */}
      <header className="mt-4">
        {!!tag && (
          <span className="inline-flex items-center rounded-full bg-slate-100 px-2.5 py-0.5 text-[11px] font-semibold uppercase tracking-wide text-slate-600 ring-1 ring-slate-200">
            {tag}
          </span>
        )}
        <h1 className="mt-3 text-3xl md:text-4xl font-extrabold tracking-tight text-slate-900">
          {title}
        </h1>
        {!!summary && <p className="mt-2 text-slate-600">{summary}</p>}
      </header>

      {/* Logo (optional) */}
      {cs.logo && (
        <div className="mt-8 grid place-items-center">
          <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm ring-1 ring-black/5">
            <img
              src={cs.logo}
              alt={title}
              loading="lazy"
              className="max-h-20 max-w-[70vw] object-contain"
            />
          </div>
        </div>
      )}

      {/* Result (optional) */}
      {cs.result && (
        <p className="mt-6 inline-flex items-center gap-2 rounded-full bg-emerald-50 px-3 py-1 text-[12px] font-semibold text-emerald-700 ring-1 ring-emerald-200">
          <span className="inline-block size-1.5 rounded-full bg-emerald-400" />
          {cs.result}
        </p>
      )}

      {/* Body */}
      <div className="mt-6 rounded-2xl border border-slate-200 bg-white p-6 shadow-sm">
        <div className="prose prose-slate max-w-none">
          <p className="whitespace-pre-line">{body}</p>
        </div>
      </div>

      {/* Footer actions */}
      <div className="mt-8 flex flex-wrap items-center gap-3">
        {cs.url && (
          <a
            href={cs.url}
            target="_blank"
            rel="noopener noreferrer"
            className="rounded-xl px-5 py-3 font-medium ring-1 ring-slate-300 hover:bg-slate-50"
          >
            {t("projects.visit")}
          </a>
        )}
        <LocalizedLink
          to="/contact"
          className="rounded-xl bg-indigo-600 px-5 py-3 text-white font-medium hover:bg-indigo-500"
        >
          {t("projects.cta") || "Start your project"}
        </LocalizedLink>
      </div>
    </section>
  );
}
